import {
  Signal,
  WritableSignal,
  computed,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { CourseVariablesDataService } from '../../../organization/organization/services/course-variables-data.service';
import {
  CourseVariable,
  LanguageCode,
} from '../../../organization/organization/models/CourseVariables';
import { Observable, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CourseVariablesService {
  private readonly courseVariablesDataService = inject(
    CourseVariablesDataService,
  );

  private readonly variableStore: Map<string, WritableSignal<string>> =
    new Map();
  private readonly variableMeta: Map<
    string,
    { key: string; languageCode?: LanguageCode }
  > = new Map();
  private previousVariables: CourseVariable[] = [];
  private readonly _changeCount = signal(0);
  readonly variablesChanged = computed(() => this.changeCount() > 0);
  readonly changeCount: Signal<number> = this._changeCount.asReadonly();

  private storeKey(key: string, languageCode?: LanguageCode): string {
    return languageCode ? `${key}::${languageCode}` : key;
  }

  private initialiseVariableSignal(
    sk: string,
    key: string,
    value: string,
    languageCode?: LanguageCode,
  ) {
    this.variableStore.set(sk, signal(value));
    this.variableMeta.set(sk, { key, languageCode });
  }

  /** this method always returns the same instance of the signal */
  getSignal(key: string, languageCode?: LanguageCode): Signal<string> {
    const sk = this.storeKey(key, languageCode);
    if (!this.variableStore.has(sk)) {
      this.initialiseVariableSignal(sk, key, '', languageCode);
    }

    // is disabled since we know it is not null
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.variableStore.get(sk)!.asReadonly();
  }

  initializeStore(variables: CourseVariable[]): void {
    this.previousVariables = [...variables];
    this.resetVariables();
  }

  updateVariable(
    key: string,
    value: string,
    languageCode?: LanguageCode,
  ): void {
    const sk = this.storeKey(key, languageCode);
    const sv = this.variableStore.get(sk);
    if (sv) {
      sv.set(value);
    } else {
      this.initialiseVariableSignal(sk, key, value, languageCode);
    }

    const count = this.getChangedVariables().length;
    this._changeCount.set(count);
  }

  resetVariables(): void {
    const previousMap = new Map(
      this.previousVariables.map((v) => [
        this.storeKey(v.key, v.languageCode),
        v,
      ]),
    );
    this.variableStore.forEach((sig, sk) => {
      const prev = previousMap.get(sk);
      sig.set(prev ? (prev.value ?? '') : '');
    });

    this._changeCount.set(0);
  }

  saveVariables(): Observable<void> {
    const changes = this.getChangedVariables();
    if (changes.length <= 0) {
      throw new Error('No changes to save');
    }

    return this.courseVariablesDataService.setCourseVariables(changes).pipe(
      tap({
        next: () => {
          this.initializeStore(this.getAllVariables());
        },
      }),
    );
  }

  private getChangedVariables(): CourseVariable[] {
    const previousSKs = new Set(
      this.previousVariables.map((v) => this.storeKey(v.key, v.languageCode)),
    );

    const changed = this.previousVariables.flatMap((v) => {
      const sig = this.variableStore.get(this.storeKey(v.key, v.languageCode));
      if (sig === undefined || sig() === (v.value ?? '')) {
        return [];
      }
      const current = sig();

      return [
        {
          key: v.key,
          value: current === '' ? null : current,
          languageCode: v.languageCode,
        },
      ];
    });

    const added = [...this.variableStore.entries()].flatMap(([sk, sig]) => {
      if (previousSKs.has(sk) || sig() === '') {
        return [];
      }
      // we got the store keys from the store so the value must exist
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const meta = this.variableMeta.get(sk)!;

      return [{ key: meta.key, value: sig(), languageCode: meta.languageCode }];
    });

    return [...changed, ...added];
  }

  private getAllVariables(): CourseVariable[] {
    return [...this.variableStore.entries()]
      .filter(([_, sig]) => sig() !== '')
      .map(([sk, sig]) => {
        // we got the store keys from the store so the value must exist
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const meta = this.variableMeta.get(sk)!;
        return { key: meta.key, value: sig(), languageCode: meta.languageCode };
      });
  }
}
